<script setup lang="ts">
import { reactive, ref } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import axios from "axios";
import router from "../router";
import OTPVerification from './OTPVerification.vue';
import AuthSlideshow from './AuthSlideshow.vue';
import type { EpPropMergeType } from 'element-plus/es/utils';

interface RuleForm {
  email: string; password: string; contact_name: string;
  business_name: string; phone_no: string; accept_terms: boolean;
}

const loading = ref(false);
const formSize = ref<EpPropMergeType<StringConstructor, "" | "default" | "small" | "large", unknown>>("default");
const formError = ref("");
const results = ref();
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive<RuleForm>({
  email: "", password: "", contact_name: "", business_name: "", phone_no: "", accept_terms: false,
});
const showOTP = ref(false);
const otpEmail = ref('');

const validateReadTerms = (rule: any, value: any, callback: any) => {
  if (value === false) callback(new Error("Please accept the terms and conditions"));
  else callback();
};
const validatePhone = (rule: any, value: any, callback: any) => {
  if (value === "") callback(new Error("Please enter your phone number"));
  else if (!results.value?.isValid) callback(new Error("Please enter a valid phone number"));
  else callback();
};

const rules = reactive<FormRules<RuleForm>>({
  contact_name:  [{ required: true, message: "Please enter your name", trigger: "blur" }],
  business_name: [{ required: true, message: "Please enter your business name", trigger: "blur" }],
  email:         [{ required: true, message: "Please enter your email address", trigger: "blur" }],
  phone_no:      [{ validator: validatePhone, trigger: "blur" }],
  password:      [{ required: true, message: "Please enter your password", trigger: "blur" }],
  accept_terms:  [{ validator: validateReadTerms, trigger: "blur" }],
});

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl || loading.value) return;
  formError.value = "";
  await formEl.validate((valid) => {
    if (valid) {
      loading.value = true;
      void postData();
    }
  });
};

const postData = async () => {
  try {
    const response = await axios.post(`${import.meta.env.VITE_ONBOARDING_SERVICE}signup`, {
      email: ruleForm.email, password: ruleForm.password, name: ruleForm.contact_name,
      company: ruleForm.business_name, phone: ruleForm.phone_no,
    });
    if (response.status == 200 && response.data.status) {
      otpEmail.value = ruleForm.email;
      showOTP.value = true;
    } else {
      formError.value = "Signup failed, please try again.";
    }
  } catch {
    formError.value = "Something went wrong, please try again.";
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="auth-page">
    <AuthSlideshow />
    <div class="auth-right">
      <div class="form-container" v-if="!showOTP">
        <div class="form-header">
          <h2 class="form-title">Create an account</h2>
          <p class="form-subtitle">Join businesses that trust us with their deliveries.</p>
        </div>
        <el-form ref="ruleFormRef" :model="ruleForm" :rules="rules" @submit.prevent
          label-width="0px" class="auth-form" :size="formSize" v-loading="loading">
          <div v-if="formError" class="error-banner">
            <span class="error-icon">!</span>{{ formError }}
          </div>
          <div class="field-group">
            <label class="field-label">Full name</label>
            <el-form-item prop="contact_name">
              <el-input v-model="ruleForm.contact_name" placeholder="Jane Doe" autocomplete="name" />
            </el-form-item>
          </div>
          <div class="field-group">
            <label class="field-label">Business name</label>
            <el-form-item prop="business_name">
              <el-input v-model="ruleForm.business_name" placeholder="Acme Ltd." autocomplete="organization" />
            </el-form-item>
          </div>
          <div class="field-group">
            <label class="field-label">Email address</label>
            <el-form-item prop="email">
              <el-input v-model="ruleForm.email" placeholder="you@company.com" autocomplete="email" />
            </el-form-item>
          </div>
          <div class="field-group">
            <label class="field-label">Phone number</label>
            <el-form-item prop="phone_no">
              <input type="text" style="display:none" autocomplete="username" />
              <input type="password" style="display:none" autocomplete="new-password" />
              <MazPhoneNumberInput
                v-model="ruleForm.phone_no" defaultCountryCode="KE"
                :only-countries="['KE', 'UG', 'TZ', 'NG', 'ZA']"
                @update="results = $event" :success="results?.isValid"
                class="phone-input" autocomplete="off"
              />
            </el-form-item>
          </div>
          <div class="field-group">
            <label class="field-label">Password</label>
            <el-form-item prop="password">
              <el-input type="password" v-model="ruleForm.password" placeholder="Create a password" autocomplete="off" show-password />
            </el-form-item>
          </div>
          <div class="field-group terms-group">
            <el-form-item prop="accept_terms">
              <label class="terms-label">
                <el-checkbox v-model="ruleForm.accept_terms" size="large" />
                <span class="terms-text">
                  I have read and accepted the
                  <a href="#" @click.prevent class="terms-link">terms and conditions</a>
                </span>
              </label>
            </el-form-item>
          </div>
          <el-form-item>
            <button type="button" class="submit-btn" @click.prevent="submitForm(ruleFormRef)" :disabled="loading">
              <span v-if="!loading">Create account</span><span v-else>Creating account…</span>
            </button>
          </el-form-item>
          <div class="bottom-link">
            Already have an account?
            <router-link to="/login" class="nav-link-inline">Sign in</router-link>
          </div>
        </el-form>
      </div>
      <OTPVerification
        v-if="showOTP" :email="otpEmail" context="signup"
        @verified="() => { showOTP = false; router.push('/login'); }"
        @close="() => { showOTP = false; }"
      />
    </div>
  </div>
</template>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display&display=swap');

.auth-page { display: flex; min-height: 100vh; font-family: 'DM Sans', sans-serif; }

.auth-right {
  flex: 1; min-width: 0; display: flex; align-items: center;
  justify-content: center; background: #fafafa; padding: 3rem 2rem;
}

.form-container { width: 100%; max-width: 400px; animation: fadeUp 0.4s ease both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.form-header { margin-bottom: 2.5rem; }
.form-title { font-family: 'DM Sans', sans-serif; font-size: 2rem; font-weight: 700; color: #0f1c2e; margin: 0 0 0.5rem 0; letter-spacing: -0.02em; }
.form-subtitle { font-size: 0.9rem; color: #8a94a6; margin: 0; line-height: 1.6; }

.error-banner { display: flex; align-items: center; gap: 0.6rem; background: #fff5f5; border: 1px solid #fdd; border-radius: 8px; padding: 0.75rem 1rem; font-size: 0.85rem; color: #c0392b; margin-bottom: 1.5rem; }
.error-icon { width: 18px; height: 18px; background: #e74c3c; color: white; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.7rem; font-weight: 700; flex-shrink: 0; }

.field-group { margin-bottom: 1.25rem; }
.label-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.4rem; }
.field-label { display: block; font-size: 0.82rem; font-weight: 500; color: #374151; margin-bottom: 0.4rem; letter-spacing: 0.01em; }
.forgot-link { font-size: 0.8rem; color: #12284A; text-decoration: none; opacity: 0.7; transition: opacity 0.15s; }
.forgot-link:hover { opacity: 1; }

.auth-form :deep(.ep-input__wrapper),
.auth-form :deep(.el-input__wrapper) { border-radius: 8px !important; border: 1.5px solid #e5e7eb !important; box-shadow: none !important; background: white !important; transition: border-color 0.2s; }
.auth-form :deep(.ep-input__wrapper:hover),
.auth-form :deep(.el-input__wrapper:hover) { border-color: #b0bec5 !important; }
.auth-form :deep(.ep-input__wrapper.is-focus),
.auth-form :deep(.el-input__wrapper.is-focus) { border-color: #12284A !important; }
.auth-form :deep(.ep-form-item),
.auth-form :deep(.el-form-item) { margin-bottom: 0; }
.auth-form :deep(input:-webkit-autofill),
.auth-form :deep(input:-webkit-autofill:hover),
.auth-form :deep(input:-webkit-autofill:focus) { -webkit-box-shadow: 0 0 0px 1000px white inset !important; box-shadow: 0 0 0px 1000px white inset !important; -webkit-text-fill-color: #374151 !important; transition: background-color 5000s ease-in-out 0s; }

.phone-input { width: 100%; }
.auth-form :deep(.m-phone-number-input) { width: 100%; }

.terms-group { margin-top: 0.5rem; }
.terms-label { display: flex; align-items: flex-start; gap: 0.6rem; cursor: pointer; }
.terms-text { font-size: 0.84rem; color: #374151; line-height: 1.5; margin-top: 2px; }
.terms-link { color: #12284A; text-decoration: underline; }

.submit-btn { width: 100%; height: 46px; background: #12284A; color: white; border: none; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 500; cursor: pointer; margin-top: 0.75rem; transition: background 0.2s, transform 0.1s; }
.submit-btn:hover:not(:disabled) { background: #1a3a6a; }
.submit-btn:active:not(:disabled) { transform: scale(0.99); }
.submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.bottom-link { text-align: center; margin-top: 1.75rem; font-size: 0.87rem; color: #8a94a6; padding-bottom: 1rem; }
.nav-link-inline { color: #12284A; font-weight: 500; text-decoration: none; margin-left: 0.25rem; }
.nav-link-inline:hover { text-decoration: underline; }
.nav-link { font-size: 0.87rem; color: #12284A; text-decoration: none; opacity: 0.7; transition: opacity 0.15s; }
.nav-link:hover { opacity: 1; }

.success-block { text-align: center; animation: fadeUp 0.4s ease both; }
.success-icon { width: 56px; height: 56px; background: #12284A; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; margin: 0 auto 1.5rem; }
.success-block .form-title { margin-bottom: 0.75rem; }
.success-block .form-subtitle { margin-bottom: 2rem; }
.back-link { font-size: 0.87rem; color: #12284A; text-decoration: none; font-weight: 500; }
.back-link:hover { text-decoration: underline; }

@media (max-width: 900px) {
  .auth-right { background: white; }
}
</style>